/**
 * @element qds-notification-tray
 * 
 * A notification tray component that displays a bell icon with notifications.
 * 
 * @attr {string} [readonly] count - Total number of notifications (read-only, automatically updated)
 * @attr {string} notifications - JSON string of notification objects
 * @attr {string} more-label - Text to display for the "more" link
 * @attr {string} more-url - URL for the "more" link
 * 
 * @property {Array<Object>} notifications - Array of notification objects with fields: { id, title, message, timestamp, read, type }
 * @property {boolean} isOpen - Whether the notification panel is open
 * @property {number} [readonly] count - Total number of notifications (read-only)
 * 
 * @method addNotification(notification) - Add a notification and return its ID
 * @method removeNotification(id) - Remove a notification by ID
 * @method clearNotifications() - Remove all notifications
 * 
 * @csspart button - The button container
 * @csspart icon - The notification bell icon
 * @csspart badge - The notification count badge
 * @csspart panel - The notification panel container
 * @csspart notification - Individual notification item
 * @csspart notification-title - The notification title
 * @csspart notification-message - The notification message content
 * @csspart notification-time - The notification timestamp
 * @csspart divider - The divider line between notifications
 * @csspart more-link - The "more" link at the bottom of the panel
 */

import { adoptPrimeIconsStyles, getBaseButtonStyles, loadBaseCSS, setupButtonEvents } from '../shared/button-utils.js';
import { Notification } from '../../types/index.js';

loadBaseCSS();

class QdsNotificationTray extends HTMLElement {
  // Private properties
  private _notifications: Notification[];
  private _isOpen: boolean;
  private _nextId: number;
  private _moreLabel: string;
  private _moreUrl: string;
  private _count: number;
  private _ignoreCountAttributeChange: boolean;
  private _cleanup: (() => void) | null;
  private _boundOutsideClickHandler: (event: MouseEvent) => void;
  private _boundToggleHandler: (event: MouseEvent) => void;

  static get observedAttributes(): string[] {
    return ['notifications', 'more-label', 'more-url'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Initialize properties
    this._notifications = [];
    this._isOpen = false;
    this._nextId = 1;
    this._moreLabel = '';
    this._moreUrl = '';
    this._count = 0;
    this._ignoreCountAttributeChange = false;
    this._cleanup = null;
    this._boundOutsideClickHandler = this._handleOutsideClick.bind(this);
    this._boundToggleHandler = this._handleToggle.bind(this);

    this.render();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;

    // Set up event handling using the shared utility
    this._cleanup = setupButtonEvents(this);

    // Add click handler for the button
    const button = this.shadowRoot?.querySelector('#notification-button') as HTMLButtonElement | null;
    if (button) {
      button.addEventListener('click', this._boundToggleHandler);
    }

    // Close panel when clicking outside
    document.addEventListener('click', this._boundOutsideClickHandler);
  }

  disconnectedCallback(): void {
    // Clean up event listeners
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = null;
    }
    
    // Remove document click listener
    document.removeEventListener('click', this._boundOutsideClickHandler);
    
    // Remove button click listener
    const button = this.shadowRoot?.querySelector('#notification-button') as HTMLButtonElement | null;
    if (button) {
      button.removeEventListener('click', this._boundToggleHandler);
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'notifications':
        try {
          this._notifications = JSON.parse(newValue || '[]');
          // Update count when notifications change
          this._updateCount();
        } catch (e) {
          console.error('Invalid notifications JSON:', e);
          this._notifications = [];
        }
        break;
      case 'more-label':
        this._moreLabel = newValue || '';
        break;
      case 'more-url':
        this._moreUrl = newValue || '';
        break;
    }

    this.render();
  }
  
  private _updateCount(): void {
    // Count is the total number of notifications
    const totalCount = this._notifications.length;
    this._count = totalCount;
    // Update the count attribute without triggering attributeChangedCallback
    if (this.getAttribute('count') !== String(totalCount)) {
      this._ignoreCountAttributeChange = true;
      this.setAttribute('count', totalCount.toString());
      this._ignoreCountAttributeChange = false;
    }
  }

  get count(): number {
    return this._count;
  }

  // Make count a read-only property
  // No setter for count

  get notifications(): Notification[] {
    return [...this._notifications];
  }

  set notifications(value: Notification[]) {
    if (Array.isArray(value)) {
      this.setAttribute('notifications', JSON.stringify(value));
      // The count will be updated in attributeChangedCallback
    }
  }

  get moreLabel(): string {
    return this._moreLabel;
  }

  set moreLabel(value: string) {
    this.setAttribute('more-label', value);
  }

  get moreUrl(): string {
    return this._moreUrl;
  }

  set moreUrl(value: string) {
    this.setAttribute('more-url', value);
  }

  private _handleToggle(event: MouseEvent): void {
    event.stopPropagation();
    this._isOpen = !this._isOpen;
    this.render();
    
    // Add a small delay to allow DOM to update before focusing
    if (this._isOpen) {
      setTimeout(() => {
        const panel = this.shadowRoot?.querySelector('#notification-panel') as HTMLElement | null;
        if (panel) {
          panel.setAttribute('tabindex', '0');
          panel.focus();
        }
      }, 50);
    }
  }

  private _handleOutsideClick(event: MouseEvent): void {
    // Only close if click is outside both the button and panel
    // We need to check if the click target is contained in this component
    const target = event.target as Node;
    if (!this.contains(target) && this._isOpen) {
      this._isOpen = false;
      this.render();
    }
  }

  addNotification(notification: Partial<Notification>): string {
    if (!notification || (!notification.message && !notification.title)) {
      throw new Error('Notification must have message or title content');
    }

    const id = notification.id || `notification-${this._nextId++}`;
    const existingIndex = this._notifications.findIndex(n => n.id === id);

    if (existingIndex >= 0) {
      // Update existing notification
      this._notifications[existingIndex] = { ...notification, id } as Notification;
    } else {
      // Add new notification
      this._notifications.unshift({ ...notification, id } as Notification);
    }

    // Update count
    this._updateCount();

    this.render();
    return id;
  }

  removeNotification(id: string): void {
    const index = this._notifications.findIndex(n => n.id === id);
    if (index >= 0) {
      this._notifications.splice(index, 1);
      
      // Update count
      this._updateCount();
      
      this.render();
    }
  }

  clearNotifications(): void {
    this._notifications = [];
    this._count = 0;
    this.setAttribute('count', '0');
    this.render();
  }

  private _formatTime(timestamp: string | Date | undefined): string {
    try {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      
      return date.toLocaleDateString();
    } catch (e) {
      console.error('Error formatting time:', e);
      return '';
    }
  }

  private _getTypeIcon(type: string): string {
    switch (type) {
      case 'success':
        return '<i class="pi pi-check-circle notification-type-icon notification-type-icon--success"></i>';
      case 'warning':
        return '<i class="pi pi-exclamation-triangle notification-type-icon notification-type-icon--warning"></i>';
      case 'error':
        return '<i class="pi pi-times-circle notification-type-icon notification-type-icon--error"></i>';
      case 'info':
      default:
        return '<i class="pi pi-info-circle notification-type-icon notification-type-icon--info"></i>';
    }
  }

  render(): void {
    if (!this.shadowRoot) return;
    
    const styles = `
      ${getBaseButtonStyles()}
      
      :host {
        --_notification-badge-background: var(--badge-bg-color, var(--theme-error, var(--brand-red)));
        --_notification-badge-text: var(--badge-text-color, var(--theme-text-on-accent-primary, var(--theme-text-inverse)));
        --_notification-panel-background: var(--panel-bg-color, var(--theme-background-elevated, var(--theme-background-primary)));
        --_notification-panel-border: var(--panel-border-color, var(--theme-border-primary));
        --_notification-panel-shadow: var(--panel-shadow, var(--theme-shadow-sm));
        --_notification-divider: var(--divider-color, var(--theme-border-primary));
        --_notification-title: var(--title-color, var(--theme-text-primary));
        --_notification-message: var(--message-color, var(--theme-text-primary));
        --_notification-time: var(--time-color, var(--theme-text-secondary));
        --_notification-empty: var(--empty-color, var(--theme-text-secondary));
        --_notification-link: var(--link-color, var(--theme-accent-primary, var(--brand-middle-blue)));
        --_notification-success: var(--success-color, var(--theme-success, var(--brand-green)));
        --_notification-warning: var(--warning-color, var(--theme-warning, var(--brand-orange)));
        --_notification-error: var(--error-color, var(--theme-error, var(--brand-red)));
        --_notification-info: var(--info-color, var(--theme-info, var(--brand-middle-blue)));
        display: inline-block;
        position: relative;
        font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
      }
      
      .badge {
        position: absolute;
        top: 3px;
        right: 3px;
        background-color: var(--_notification-badge-background);
        color: var(--_notification-badge-text);
        font-size: var(--theme-font-size-xs, 12px);
        min-width: 16px;
        height: 16px;
        border-radius: var(--theme-radius-md, 8px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
        font-weight: var(--theme-font-weight-semibold, 600);
        box-sizing: border-box;
      }

      .tray-shell {
        position: relative;
      }

      .notification-panel {
        position: absolute;
        top: 100%;
        right: 0;
        width: 300px;
        max-height: 400px;
        background-color: var(--_notification-panel-background);
        border: 1px solid var(--_notification-panel-border);
        border-radius: var(--theme-radius-sm, 4px);
        box-shadow: var(--_notification-panel-shadow);
        z-index: 1000;
        overflow-y: auto;
        display: none;
      }
      
      .notification-panel.open {
        display: block;
      }

      .notification {
        padding: var(--theme-space-3, 12px);
        border-bottom: 1px solid var(--_notification-divider);
        display: flex;
        align-items: flex-start;
        gap: var(--theme-space-2, 8px);
      }

      .notification:last-child {
        border-bottom: none;
      }

      .notification-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .notification-content {
        flex-grow: 1;
        overflow-wrap: break-word;
        word-break: break-word;
      }

      .notification-title {
        margin: 0 0 4px 0;
        font-size: var(--theme-font-size-sm, 14px);
        font-weight: var(--theme-font-weight-semibold, 600);
        line-height: 1.4;
        color: var(--_notification-title);
      }
      
      .notification-message {
        margin: 0;
        font-size: var(--theme-font-size-sm, 14px);
        line-height: 1.4;
        color: var(--_notification-message);
      }

      .notification-time {
        font-size: var(--theme-font-size-xs, 12px);
        color: var(--_notification-time);
        margin-top: 4px;
      }

      .empty-state {
        padding: var(--theme-space-5, 20px);
        text-align: center;
        color: var(--_notification-empty);
      }
      
      .more-link {
        display: block;
        padding: var(--theme-space-3, 12px);
        text-align: center;
        border-top: 1px solid var(--_notification-divider);
        background-color: var(--_notification-panel-background);
      }

      .more-link a {
        color: var(--_notification-link);
        text-decoration: none;
        font-size: var(--theme-font-size-sm, 14px);
      }

      .more-link a:hover {
        text-decoration: underline;
      }

      .more-link a:focus-visible {
        outline: 2px solid var(--theme-accent-primary, var(--brand-middle-blue));
        outline-offset: 2px;
        border-radius: var(--theme-radius-sm, 4px);
      }

      .bell-icon {
        font-size: var(--icon-size, 20px);
        color: currentColor;
      }

      .notification-type-icon {
        font-size: var(--theme-font-size-md, 16px);
      }

      .notification-type-icon--success {
        color: var(--_notification-success);
      }

      .notification-type-icon--warning {
        color: var(--_notification-warning);
      }

      .notification-type-icon--error {
        color: var(--_notification-error);
      }

      .notification-type-icon--info {
        color: var(--_notification-info);
      }

      @media (max-width: 480px) {
        .notification-panel {
          top: auto;
          bottom: calc(100% + 8px);
          left: 0;
          right: auto;
          width: min(300px, calc(100vw - 24px));
          max-height: min(60vh, 400px);
        }
      }
    `;

    // Use the internal count property, which is always up-to-date
    const count = this._count;

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>

      <div class="tray-shell">
        <button id="notification-button" part="button" aria-label="Notifications" aria-expanded="${this._isOpen ? 'true' : 'false'}" aria-controls="notification-panel">
          <i class="pi pi-bell bell-icon" part="icon"></i>
          ${count > 0 ? `<span class="badge" part="badge">${count > 99 ? '99+' : count}</span>` : ''}
        </button>
        
        <div id="notification-panel" class="notification-panel ${this._isOpen ? 'open' : ''}" part="panel" role="region" aria-label="Notifications">
          ${this._notifications.length === 0 ? 
            `<div class="empty-state">No notifications</div>` : 
            this._notifications.map(notification => `
              <div class="notification" part="notification">
                <div class="notification-icon" part="notification-icon">
                  ${this._getTypeIcon(notification.type)}
                </div>
                <div class="notification-content">
                  ${notification.title ? 
                    `<div class="notification-title" part="notification-title">${notification.title}</div>` : ''}
                  ${notification.message ? 
                    `<div class="notification-message" part="notification-message">${notification.message}</div>` : ''}
                  ${notification.timestamp ? 
                    `<div class="notification-time" part="notification-time">${this._formatTime(notification.timestamp)}</div>` : ''}
                </div>
              </div>
            `).join('')
          }
          ${this._moreLabel && this._moreUrl ? `
            <div class="more-link" part="more-link">
              <a href="${this._moreUrl}" target="_blank" rel="noopener noreferrer">${this._moreLabel}</a>
            </div>
          ` : ''}
        </div>
      </div>
    `;
    void adoptPrimeIconsStyles(this.shadowRoot);

    // Re-add event listener since we're re-rendering the DOM
    if (this.isConnected) {
      this._cleanup = setupButtonEvents(this);
      const button = this.shadowRoot.querySelector('#notification-button') as HTMLButtonElement | null;
      if (button) {
        button.addEventListener('click', this._boundToggleHandler);
      }
      
      // Add keyboard navigation support
      const panel = this.shadowRoot.querySelector('#notification-panel') as HTMLElement | null;
      if (panel) {
        panel.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            this._isOpen = false;
            this.render();
            const button = this.shadowRoot?.querySelector('#notification-button') as HTMLButtonElement | null;
            if (button) button.focus();
          }
        });
      }
    }
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-notification-tray')) {
  customElements.define('qds-notification-tray', QdsNotificationTray);
}

export { QdsNotificationTray };
